import React from 'react';
import {
  ListItem,
  ListItemButton,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import {
  Medication as MedicationIcon,
  Task as TaskIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Notifications as SystemIcon,
  Message as CommunicationIcon,
} from '@mui/icons-material';
import { Notification } from '../../services/notifications';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

interface NotificationItemProps {
  notification: Notification;
  onClick: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onClick }) => {
  const getIcon = () => {
    switch (notification.type) {
      case 'medication':
        return <MedicationIcon fontSize="small" />;
      case 'task':
        return <TaskIcon fontSize="small" />;
      case 'alert':
        return <WarningIcon fontSize="small" />;
      case 'system':
        return <SystemIcon fontSize="small" />;
      case 'communication':
        return <CommunicationIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  const getSeverityColor = () => {
    switch (notification.severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const getCategoryColor = () => {
    switch (notification.category) {
      case 'critical':
        return 'error';
      case 'care':
        return 'primary';
      case 'admin':
        return 'secondary';
      case 'communication':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatTime = (dateString: string) => {
    try {
      return dayjs(dateString).fromNow();
    } catch {
      return '';
    }
  };

  return (
    <ListItem
      disablePadding
      sx={{
        backgroundColor: notification.read ? 'transparent' : 'action.hover',
        borderLeft: notification.read ? 'none' : `3px solid`,
        borderLeftColor: notification.severity === 'critical' ? 'error.main' : 'primary.main',
      }}
    >
      <ListItemButton onClick={onClick} sx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
          <Box
            sx={{
              color: getSeverityColor() === 'error' ? 'error.main' : 'primary.main',
              mr: 1.5,
              mt: 0.5,
            }}
          >
            {getIcon()}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: notification.read ? 400 : 600,
                  flex: 1,
                }}
              >
                {notification.title}
              </Typography>
              <Chip
                label={notification.severity}
                size="small"
                color={getSeverityColor() as any}
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
              />
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {notification.message}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Chip
                label={notification.category}
                size="small"
                variant="outlined"
                color={getCategoryColor() as any}
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {formatTime(notification.created_at)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </ListItemButton>
    </ListItem>
  );
};

export default NotificationItem;

